import React, {useMemo} from 'react';
import moment from 'moment';
import {shortenLargeNumbers} from '../../../../helpers/shortenLargeNumbers';
import {RowData} from '../../../../components';
import {isObjectEmpty} from '../../../../helpers/isObjectEmpty';
import {displayData, getMeta} from '../utils';
import {useQuery} from 'react-query';
import {fetchEventDetails} from '../../../../requests/queries/flows';
import {useParams} from 'react-router-dom';

export const useEventDetails = () => {
  const {slug} = useParams();

  const {data: eventData, isLoading} = useQuery(['event_details', slug], () =>
    fetchEventDetails(slug),
  );

  const eventDetails = useMemo(
    () => !isLoading && eventData && eventData,
    [eventData, isLoading],
  );

  const USER = useMemo(
    () => eventDetails?.transaction_data?.event?.user,
    [eventDetails],
  );
  const TRANSACTION = useMemo(
    () => eventDetails?.transaction_data,
    [eventDetails],
  );
  const ADDRESS = useMemo(
    () => eventDetails?.transaction_data?.event?.address,
    [eventDetails],
  );
  const RANGES = useMemo(
    () => eventDetails?.evaluation_result?.ranges_used,
    [eventDetails],
  );
  const TRIGGERED_RULES = useMemo(
    () => eventDetails?.evaluation_result?.triggered_rules,
    [eventDetails],
  );
  const PAYMENT = useMemo(
    () => eventDetails?.transaction?.payment,
    [eventDetails],
  );
  const SENDER = useMemo(
    () => eventDetails?.transaction?.senderAccount,
    [eventDetails],
  );
  const DEVICE = useMemo(
    () => eventDetails?.transaction?.device,
    [eventDetails],
  );
  const RECEIVER = useMemo(
    () => eventDetails?.transaction?.receiverAccount,
    [eventDetails],
  );
  const META = useMemo(
    () => TRANSACTION?.event?.meta,
    [TRANSACTION?.event?.meta],
  );
  const BEHAVIORAL_ANALYSIS = useMemo(
    () => eventDetails?.transaction_analysis,
    [eventDetails?.transaction_analysis],
  );
  const TRANSACTION_EVENTS = useMemo(
    () => eventDetails?.transaction_data?.event,
    [eventDetails?.transaction_data?.event],
  );

  const USER_DETAILS = useMemo(
    () => [
      {
        leftText: 'Full name',
        rightText: USER?.name || '-',
      },
      {
        leftText: 'email address',
        rightText: USER?.email || '-',
      },
      {
        leftText: 'phone number',
        rightText: USER?.mobile || '-',
      },
    ],
    [USER],
  );

  const TRANSACTION_ANALYSIS = useMemo(
    () =>
      Object.entries(BEHAVIORAL_ANALYSIS || {}).map(([key, value]) => ({
        label: key?.replaceAll('_', ' '),
        value: Object.entries(value || {}).map(([vKey, vValue]) => ({
          leftText: vKey?.replaceAll('_', ' '),
          rightText: vValue?.toString(),
        })),
      })),
    [BEHAVIORAL_ANALYSIS],
  );
  const TRANSACTION_EVENT_INFO = useMemo(
    () => displayData(TRANSACTION_EVENTS),
    [TRANSACTION_EVENTS],
  );

  const BEHAVIORAL_ANALYSIS_DETAILS = useMemo(
    () => [
      {
        leftText: 'Sufficient History',
        rightText: BEHAVIORAL_ANALYSIS?.has_sufficient_history
          ? 'True'
          : 'False',
      },
      {
        leftText: '1h Transaction Velocity',
        rightText: BEHAVIORAL_ANALYSIS?.transaction_velocity_1h || '0',
      },
      {
        leftText: '24h Transaction Velocity',
        rightText: BEHAVIORAL_ANALYSIS?.transaction_velocity_24h || '0',
      },
      {
        leftText: '7d Transaction Velocity',
        rightText: BEHAVIORAL_ANALYSIS?.transaction_velocity_7d || '0',
      },
      {
        leftText: 'Avg Transaction Amount',
        rightText: shortenLargeNumbers(
          BEHAVIORAL_ANALYSIS?.avg_transaction_amount || 0,
          TRANSACTION?.currency || 'NGN',
          {startAt: 'B'},
        ),
      },
      {
        leftText: 'Max Transaction Amount',
        rightText: shortenLargeNumbers(
          BEHAVIORAL_ANALYSIS?.max_transaction_amount || 0,
          TRANSACTION?.currency || 'NGN',
          {startAt: 'B'},
        ),
      },
      {
        leftText: 'Amount Standard Deviation',
        rightText: shortenLargeNumbers(
          BEHAVIORAL_ANALYSIS?.amount_std_dev || 0,
          TRANSACTION?.currency || 'NGN',
          {startAt: 'B'},
        ),
      },
      {
        leftText: 'High Risk Ratio',
        rightText: BEHAVIORAL_ANALYSIS?.high_risk_ratio
          ? `${(BEHAVIORAL_ANALYSIS.high_risk_ratio * 100).toFixed(2)}%`
          : '0%',
      },
      {
        leftText: 'Block Ratio',
        rightText: BEHAVIORAL_ANALYSIS?.block_ratio
          ? `${(BEHAVIORAL_ANALYSIS.block_ratio * 100).toFixed(2)}%`
          : '0%',
      },
      {
        leftText: 'Unusual Amount',
        rightText: BEHAVIORAL_ANALYSIS?.is_amount_unusual ? 'True' : 'False',
      },
      {
        leftText: 'Night Time Transaction',
        rightText: BEHAVIORAL_ANALYSIS?.is_night_time ? 'True' : 'False',
      },
      {
        leftText: 'Weekend Transaction',
        rightText: BEHAVIORAL_ANALYSIS?.is_weekend ? 'True' : 'False',
      },
      {
        leftText: 'New User',
        rightText: BEHAVIORAL_ANALYSIS?.is_new_user ? 'True' : 'False',
      },
    ],
    [BEHAVIORAL_ANALYSIS, TRANSACTION?.currency],
  );

  const TRANSACTION_DETAILS = useMemo(
    () => [
      {
        leftText: 'Transaction gateway',
        rightText: TRANSACTION?.event?.transaction?.type || '-',
      },

      {
        leftText: 'transaction time',
        rightText:
          moment(
            !isNaN(TRANSACTION?.event?.transaction?.time)
              ? +TRANSACTION?.event?.transaction?.time
              : TRANSACTION?.event?.transaction?.time,
          ).format('HH:mm a') || '-',
      },
      {
        leftText: 'Amount',
        rightText: shortenLargeNumbers(
          TRANSACTION?.event?.transaction?.amount || 0,
          TRANSACTION?.currency || 'NGN',
          {startAt: 'B'},
        ),
      },
    ],
    [TRANSACTION],
  );

  const OTHER_DETAILS = getMeta(META);

  const ADDRESS_DETAILS = [
    {
      leftText: 'STREET',
      rightText: ADDRESS?.street || '-',
    },
    {
      leftText: 'COUNTRY',
      rightText: ADDRESS?.country || '-',
    },
    {
      leftText: 'CITY',
      rightText: ADDRESS?.city || '-',
    },
  ];
  const DEVICE_DETAILS = [
    {
      leftText: 'TYPE',
      rightText: DEVICE?.device_type || '-',
    },
    {
      leftText: 'MODEL',
      rightText: DEVICE?.device_model || '-',
    },
    {
      leftText: 'OS',
      rightText: DEVICE?.os || '-',
    },
    {
      leftText: 'IP ADDRESS',
      rightText: DEVICE?.ip_address || '-',
    },
  ];
  const CARD = [
    {
      leftText: 'card last four',
      rightText: PAYMENT?.card_last_four || '-',
    },
    {
      leftText: 'expired date',
      rightText: PAYMENT?.expired_date || '-',
    },
    {
      leftText: 'name of card',
      rightText: PAYMENT?.name_of_card || '-',
    },
    {
      leftText: 'payment method type',
      rightText: PAYMENT?.payment_method_type || '-',
    },
    {
      leftText: 'scheme',
      rightText: PAYMENT?.scheme || '-',
    },
  ];
  const BANK_SENDER = [
    {
      leftText: 'Account name',
      rightText: SENDER?.account_name || '-',
    },
    {
      leftText: 'Bank name',
      rightText: SENDER?.bank_name || '-',
    },
    {
      leftText: 'Account number',
      rightText: SENDER?.account_number || '-',
    },
    {
      leftText: 'Country',
      rightText: SENDER?.country || '-',
    },
  ];
  const BANK_RECEIVER = [
    {
      leftText: 'Account name',
      rightText: RECEIVER?.account_name || '-',
    },
    {
      leftText: 'Bank name',
      rightText: RECEIVER?.bank_name || '-',
    },
    {
      leftText: 'Account number',
      rightText: RECEIVER?.account_number || '-',
    },
    {
      leftText: 'Country',
      rightText: RECEIVER?.country || '-',
    },
  ];

  const RANGES_USED = [
    {
      leftText: 'ALLOWED',
      rightText: `${RANGES?.allow?.min || 0} - ${RANGES?.allow?.max || 0}`,
    },
    {
      leftText: 'REVIEW',
      rightText: `${RANGES?.review?.min || 0} - ${RANGES?.review?.max || 0}`,
    },
    {
      leftText: 'BLOCKED',
      rightText: `${RANGES?.block?.min || 0} - ${RANGES?.block?.max || 0}`,
    },
  ];

  const ALL_DETAILS_DATA = [
    {
      title: 'User Data',
      data: USER_DETAILS?.map((item, index) => (
        <RowData key={index} {...item} />
      )),
    },
    {
      title: 'Transaction Details',
      data: TRANSACTION_DETAILS?.map((item, index) => (
        <RowData key={index} {...item} />
      )),
    },
    {
      title: 'Address Details',
      data: ADDRESS_DETAILS?.map((item, index) => (
        <RowData key={index} {...item} />
      )),
    },
    ...(!isObjectEmpty(PAYMENT)
      ? [
          {
            title: 'Card Transaction',
            data: CARD?.map((item, index) => <RowData key={index} {...item} />),
          },
        ]
      : []),
    ...(!isObjectEmpty(SENDER)
      ? [
          {
            title: 'Sender',
            data: BANK_SENDER?.map((item, index) => (
              <RowData key={index} {...item} />
            )),
          },
        ]
      : []),
    ...(!isObjectEmpty(RECEIVER)
      ? [
          {
            title: 'Receiver',
            data: BANK_RECEIVER?.map((item, index) => (
              <RowData key={index} {...item} />
            )),
          },
        ]
      : []),
    ...(!isObjectEmpty(DEVICE)
      ? [
          {
            title: 'Device',
            data: DEVICE_DETAILS?.map((item, index) => (
              <RowData key={index} {...item} />
            )),
          },
        ]
      : []),
    ...(!isObjectEmpty(META)
      ? [
          {
            title: 'Meta',
            data: OTHER_DETAILS?.map((item, index) => (
              <RowData key={index} {...item} />
            )),
          },
        ]
      : []),
  ];
  return {
    ALL_DATA: ALL_DETAILS_DATA,
    USER,
    TRANSACTION,
    ADDRESS,
    PAYMENT,
    SENDER,
    RECEIVER,
    META,
    isLoading,
    RANGES_USED,
    TRIGGERED_RULES,
    BEHAVIORAL_ANALYSIS_DETAILS,
    TRANSACTION_ANALYSIS,
    TRANSACTION_EVENT_INFO,
  };
};
